// moms-harness:anchor v2
// JIRA: LOGISPRD-14586, LOGISPRD-14591
import { type ShipperScope, isCoMingleShipper } from '@/components/system/shipperScope'
import { SHIPPERS } from '@/utils/consts'
import { SERVICE_TYPE_OPTIONS } from './consts'

/** 등록할 수 있는 화주유형 — 화면이 고를 수 있는 유형(MWP·MLT)과 같다. BE `SUPPORTED_SERVICE_TYPES` 와 같은 범위다 */
export const REGISTRABLE_SERVICE_TYPES: ReadonlySet<string> = new Set(SERVICE_TYPE_OPTIONS.map((o) => o.value))

/**
 * 등록(추가·엑셀 업로드·신규 저장)을 막을 사유. 허용이면 `null` (LOGISPRD-14586).
 *
 * MUSINSA 조회 전용 판정은 FF와 관계없이 계정 범위로 적용하므로 `scope`는 필수다.
 *
 * ### 왜 화면이 막는가 — BE 가 이미 막는데
 *
 * BE 일괄 등록은 MWP·MLT `usable` 화주가 아니면 400 이다. 그런데 화면은 「화주가 골라졌는가」만 보고 행 추가·
 * 엑셀 업로드를 열어 두어, MWP·MLT 가 아닌 화주(예: M1P 화주 계정의 자기 화주)로 행을 다 채운 뒤 **저장에서야**
 * 실패를 봤다. 입력 전에 알린다.
 *
 * ### 화주 옵션 목록이 완전할 때만 판정한다
 *
 * `serviceType` 은 화주 옵션 목록(MWP·MLT 유형만 조회한 결과)에서 찾은 값이다. 목록이 **완전하면**(로딩이 끝났고
 * 두 유형 조회가 모두 성공) 목록에 없는 화주는 MWP·MLT 화주가 아니다. 그러나 로딩 중이거나 한 유형이라도 실패했으면
 * 「없다」가 「모른다」일 뿐이라(`useGetShipperOptions` KDoc — 조용한 누락), 그때는 **막지 않고** BE 의 400 에 맡긴다.
 * 정상 MWP 화주를 「MWP·MLT 아님」으로 막는 오판이 더 나쁘다.
 */
export const registrationBlockReason = (shipperCode: string, serviceType: string | undefined, optionsComplete: boolean, scope: ShipperScope): string | null => {
  if (isCoMingleShipper(scope) && shipperCode === SHIPPERS.MUSINSA) return 'MUSINSA는 조회만 가능합니다. 추가·엑셀 업로드·신규 저장은 할 수 없습니다.'
  if (!shipperCode) return null
  if (serviceType && REGISTRABLE_SERVICE_TYPES.has(serviceType)) return null
  if (!optionsComplete) return null
  return `상품속성은 MWP·MLT 화주만 추가·업로드·저장할 수 있습니다. (선택한 화주 ${shipperCode} 은(는) MWP·MLT 화주가 아닙니다)`
}

/**
 * 등록 대상 화주 — **조회한 화주가 우선이고, 조회 전이면 필터에서 지금 고른 화주**다 (LOGISPRD-14591).
 *
 * 초판은 마지막 [조회] 조건(`searchParams.shipperCode`)만 봐서, 화주유형·화주명을 골라 놓고 조회 전에 [엑셀 업로드]·
 * [추가]를 누르면 「화주명을 선택한 뒤 …」로 막혔다. 조회한 화주가 있으면 그것을 쓰는 이유는 표에 보이는 행이 그
 * 화주의 것이라서다 — 조회 뒤 필터만 바꾼 화주로 행을 늘리면 한 표에 두 화주가 섞인다.
 */
export const resolveTargetShipper = (searchedShipperCode: string | undefined, selectedShipperCode: string | undefined): string =>
  searchedShipperCode || selectedShipperCode || ''

/**
 * 신규 행들의 등록 화주 — 행이 만들어질 때 기록된 `shipper_code` 로 정한다 (LOGISPRD-14591).
 *
 * 등록 대상 화주는 조회 전이면 필터 선택을 따라 바뀐다. 그래서 저장 시점의 대상 화주로 보내면, 화주 A 로 행을 만든 뒤
 * 필터만 B 로 바꾸고 저장했을 때 **A 의 행이 B 로 채번된다.** 행에 남은 화주로 보내고, 화주가 섞였으면 막는다.
 * 섞였으면 `error` 에 안내 문구가 든다. 신규 행이 없으면 `shipperCode` 가 빈 문자열이다.
 */
export const newRowsShipper = (rows: { shipper_code: string }[]): { shipperCode: string; error: string | null } => {
  const codes = [...new Set(rows.map((r) => r.shipper_code).filter(Boolean))]
  if (codes.length > 1) return { shipperCode: '', error: `신규 행의 화주가 섞여 있습니다(${codes.join(', ')}). 등록은 한 화주씩 저장하세요.` }
  return { shipperCode: codes[0] ?? '', error: null }
}
